<template>
  <Transition name="slide-up">
    <div
      v-if="!isDesktop && isVisible"
      class="mobile-bottom-navigation"
    >
      <NuxtLink
        v-for="item in menuItems"
        :key="item.path"
        :to="item.path"
        class="nav-item"
        :class="{ active: isActive(item.path, item.exactMatch) }"
        @click="emit('navigate', item.path)"
      >
        <div class="nav-item-icon-wrapper">
          <component
            :is="item.icon"
            :fill="isActive(item.path, item.exactMatch) ? '#3176FF' : 'rgba(58, 61, 68, 0.6)'"
            :class="[
              'nav-item-icon',
              isActive(item.path, item.exactMatch) ? 'text-primary fill-primary' : 'text-txopacity fill-txopacity'
            ]"
          />
          <div
            v-if="item.badge && item.badge > 0"
            class="nav-item-badge"
            :class="{
              'badge-active': item.badgeType === 'blue' || (isActive(item.path, item.exactMatch) && item.badgeType !== 'green'),
              'badge-green': item.badgeType === 'green',
              'badge-gray': item.badgeType !== 'green' && item.badgeType !== 'blue' && !isActive(item.path, item.exactMatch),
            }"
          >
            <span>{{ formatBadge(item.badge) }}</span>
            <span v-if="item.badgeNew && item.badgeNew > 0" class="badge-new">+{{ item.badgeNew }}</span>
          </div>
        </div>
        <span
          class="nav-item-label"
          :class="{ 'text-primary': isActive(item.path, item.exactMatch) }"
        >
          {{ item.label }}
        </span>
        <div
          v-if="isActive(item.path, item.exactMatch)"
          class="nav-item-indicator"
        />
      </NuxtLink>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useScreenSize } from "~/helpers/isMobile";
import type { Component } from "vue";

export interface MenuItem {
  path: string;
  label: string;
  icon: Component;
  badge?: number;
  badgeNew?: number;
  badgeType?: "green" | "gray" | "blue";
  exactMatch?: boolean;
}

interface Props {
  items: MenuItem[];
  isVisible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: true,
});

const emit = defineEmits<{ (e: "navigate", path: string): void }>();

const route = useRoute();
const { isDesktop } = useScreenSize();

const menuItems = computed(() => props.items);

const isActive = (path: string, exactMatch?: boolean) => {
  if (exactMatch) {
    return route.path === path;
  }
  return route.path === path || route.path.startsWith(path + "/");
};

const formatBadge = (value: number) => {
  if (value >= 1000000) {
    return Math.floor(value / 1000000) + "M";
  }
  if (value >= 1000) {
    return Math.floor(value / 1000) + "k";
  }
  return value.toString();
};
</script>

<style scoped lang="scss">
.mobile-bottom-navigation {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 58px;
  background: white;
  box-shadow: 0px -8px 8px -4px rgba(65, 86, 169, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-around;
  z-index: 1000;
  padding-bottom: env(safe-area-inset-bottom);
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  position: relative;
  text-decoration: none;
  padding: 8px 0;
  transition: background-color 0.2s ease;

  &.active {
    background-color: rgba(49, 118, 255, 0.05);
  }
}

.nav-item-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-item-icon {
  width: 20px;
  height: 20px;
}

.nav-item-badge {
  position: absolute;
  top: -5px;
  left: 10px;
  min-width: 14px;
  height: 14px;
  padding: 0 5px;
  border-radius: 12px;
  border: 1px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  line-height: 14px;
  white-space: nowrap;
  overflow: hidden;
  font-size: 8px;

  &.badge-active {
    background-color: #3176FF;

    span {
      color: white;
      font-size: 8px;
      line-height: 7px;
      font-weight: 600;
    }
  }

  &.badge-green {
    background-color: #32D687;

    span {
      color: white;
      font-size: 8px;
      line-height: 7px;
      font-weight: 600;
    }
  }

  &.badge-gray {
    background-color: #F0F0F0;

    span {
      color: rgba(58, 61, 68, 0.6);
      font-size: 8px;
      line-height: 7px;
      font-weight: 600;
    }
  }

  .badge-new {
    color: #32D687;
    margin-left: 1px;

    span {
      color: #F0F0F0;
      font-size: 8px;
      line-height: 7px;
      font-weight: 600;
    }
  }
}

.nav-item-label {
  font-size: 10px;
  line-height: 1.3;
  color: rgba(58, 61, 68, 0.6);
  margin-top: 5px;
  text-align: center;
}

.nav-item-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #3176FF;
}

// Transition
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
